import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getDecks, getCards, createCard, updateCard, deleteCard, aiGenerateCards, Deck, Card, GeneratedCard } from '../api';

// ─── Card edit modal ──────────────────────────────────────────────────────────

function CardModal({ deckId, card, onClose, onSaved }: { deckId: number; card?: Card; onClose: () => void; onSaved: () => void }) {
  const [front, setFront] = useState(card?.front || '');
  const [back, setBack] = useState(card?.back || '');
  const [reading, setReading] = useState(card?.reading || '');
  const [example, setExample] = useState(card?.example || '');
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (!front.trim() || !back.trim()) return;
    setLoading(true);
    if (card) {
      await updateCard(card.id, { front: front.trim(), back: back.trim(), reading: reading.trim(), example: example.trim() });
    } else {
      await createCard(deckId, { front: front.trim(), back: back.trim(), reading: reading.trim() || undefined, example: example.trim() || undefined });
    }
    setLoading(false);
    onSaved();
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>{card ? 'Sửa thẻ' : 'Thêm thẻ mới'}</h3>
        <div className="form-group">
          <label>Mặt trước (từ cần học) *</label>
          <input value={front} onChange={e => setFront(e.target.value)} placeholder="VD: 食べる hoặc Resilient" autoFocus style={{ fontSize: '1.1rem' }} />
        </div>
        <div className="form-group">
          <label>Cách đọc (Furigana / IPA – tuỳ chọn)</label>
          <input value={reading} onChange={e => setReading(e.target.value)} placeholder="VD: たべる" />
        </div>
        <div className="form-group">
          <label>Nghĩa (mặt sau) *</label>
          <input value={back} onChange={e => setBack(e.target.value)} placeholder="VD: Ăn / Kiên cường, bền bỉ" />
        </div>
        <div className="form-group">
          <label>Câu ví dụ (tuỳ chọn)</label>
          <textarea value={example} onChange={e => setExample(e.target.value)} placeholder="VD: 毎日ご飯を食べる。" />
        </div>
        <div className="form-actions">
          <button className="btn btn-ghost" onClick={onClose}>Huỷ</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading || !front.trim() || !back.trim()}>
            {loading ? 'Đang lưu...' : (card ? 'Lưu' : 'Thêm thẻ')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── AI Generate modal ────────────────────────────────────────────────────────

interface PreviewCard extends GeneratedCard { selected: boolean; }

function AIGenerateModal({ deckId, langPair, onClose, onCardsAdded }: {
  deckId: number;
  langPair: string;
  onClose: () => void;
  onCardsAdded: () => void;
}) {
  const [step, setStep] = useState<'input' | 'preview'>('input');
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cards, setCards] = useState<PreviewCard[]>([]);

  const isJa = langPair === 'ja-vi';

  const handleGenerate = async () => {
    if (!text.trim()) return;
    setLoading(true);
    setError('');
    try {
      const data = await aiGenerateCards(deckId, { text, language_pair: langPair });
      setCards(data.cards.map(c => ({ ...c, selected: true })));
      setStep('preview');
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Có lỗi xảy ra.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCards = async () => {
    const selected = cards.filter(c => c.selected);
    if (!selected.length) return;
    setLoading(true);
    setError('');
    try {
      for (const card of selected) {
        await createCard(deckId, {
          front: card.front,
          back: card.back,
          reading: card.reading || undefined,
          example: card.example || undefined,
        });
      }
      onCardsAdded();
      onClose();
    } catch (e: any) {
      setError(e.message || 'Lỗi khi lưu thẻ.');
    } finally {
      setLoading(false);
    }
  };

  const toggleCard = (i: number) => {
    setCards(prev => prev.map((c, idx) => idx === i ? { ...c, selected: !c.selected } : c));
  };

  const selectedCount = cards.filter(c => c.selected).length;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal ai-modal" onClick={e => e.stopPropagation()}>
        {step === 'input' ? (
          <>
            <div className="ai-modal-header">
              <span className="ai-modal-icon">🤖</span>
              <div>
                <h3>Tạo thẻ bằng AI</h3>
                <p className="ai-modal-sub">Nhập từ vựng, đoạn văn hoặc danh sách – AI sẽ tự tạo thẻ</p>
              </div>
            </div>

            <div className="form-group">
              <label>Nội dung đầu vào</label>
              <textarea
                className="ai-textarea"
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder={isJa
                  ? 'Ví dụ:\n食べる – ăn\n飲む\n勉強する\n\nHoặc dán đoạn văn tiếng Nhật bất kỳ...'
                  : 'Ví dụ:\nambiguous\neloquent\nperseverance\n\nHoặc dán đoạn văn tiếng Anh bất kỳ...'}
                autoFocus
              />
            </div>

            {error && <div className="alert alert-warning">{error}</div>}

            <div className="form-actions">
              <button className="btn btn-ghost" onClick={onClose}>Huỷ</button>
              <button className="btn btn-ai" onClick={handleGenerate} disabled={loading || !text.trim()}>
                {loading ? <><span className="spinner" style={{ width: 14, height: 14 }} />Đang phân tích...</> : '✨ Tạo thẻ'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="ai-modal-header">
              <span className="ai-modal-icon">✅</span>
              <div>
                <h3>Xem trước thẻ được tạo</h3>
                <p className="ai-modal-sub">Bỏ chọn những thẻ không muốn thêm ({selectedCount}/{cards.length} được chọn)</p>
              </div>
            </div>

            <div className="ai-preview-table-wrap">
              <table className="ai-preview-table">
                <thead>
                  <tr>
                    <th style={{ width: 32 }}></th>
                    <th>Mặt trước</th>
                    {isJa && <th>Đọc</th>}
                    <th>Nghĩa</th>
                    <th>Ví dụ</th>
                  </tr>
                </thead>
                <tbody>
                  {cards.map((card, i) => (
                    <tr key={i} className={card.selected ? '' : 'ai-row-deselected'} onClick={() => toggleCard(i)} style={{ cursor: 'pointer' }}>
                      <td>
                        <input type="checkbox" checked={card.selected} onChange={() => toggleCard(i)} onClick={e => e.stopPropagation()} />
                      </td>
                      <td className="card-front-cell">{card.front}</td>
                      {isJa && <td className="card-reading-cell">{card.reading || '—'}</td>}
                      <td>{card.back}</td>
                      <td style={{ color: 'var(--ink-muted)', fontSize: '0.78rem', maxWidth: 180 }}>
                        {card.example ? card.example.slice(0, 60) + (card.example.length > 60 ? '…' : '') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {error && <div className="alert alert-warning">{error}</div>}

            <div className="form-actions">
              <button className="btn btn-ghost" onClick={() => setStep('input')}>← Sửa lại</button>
              <button className="btn btn-ai" onClick={handleAddCards} disabled={loading || selectedCount === 0}>
                {loading ? <><span className="spinner" style={{ width: 14, height: 14 }} />Đang lưu...</> : `+ Thêm ${selectedCount} thẻ`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── DeckPage ─────────────────────────────────────────────────────────────────

export default function DeckPage() {
  const { id } = useParams<{ id: string }>();
  const deckId = Number(id);
  const [deck, setDeck] = useState<Deck | null>(null);
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [editCard, setEditCard] = useState<Card | undefined>();
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const [allDecks, cardList] = await Promise.all([getDecks(), getCards(deckId)]);
    setDeck(allDecks.find(d => d.id === deckId) || null);
    setCards(cardList);
    setLoading(false);
  }, [deckId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (cardId: number) => {
    if (!window.confirm('Xoá thẻ này?')) return;
    await deleteCard(cardId);
    await load();
  };

  const filtered = cards.filter(c =>
    c.front.toLowerCase().includes(search.toLowerCase()) ||
    c.back.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <div className="loading"><div className="spinner" />Đang tải...</div>;
  if (!deck) return <div className="loading">Không tìm thấy bộ từ.</div>;

  const isJa = deck.language_pair === 'ja-vi';

  return (
    <div>
      <Link to="/" className="back-link">← Quay lại</Link>

      <div className="deck-page-header">
        <div className="deck-page-title">
          <h1>{deck.name}</h1>
          <div className="lang-tag">{isJa ? '🇯🇵 Nhật – Việt' : '🇬🇧 Anh – Việt'} · {cards.length} thẻ · {deck.due_count || 0} cần ôn</div>
          {deck.description && <p style={{ color: 'var(--ink-muted)', fontSize: '0.9rem', marginTop: '4px' }}>{deck.description}</p>}
        </div>
        <div className="deck-study-btns">
          <Link to={`/deck/${deckId}/review`} className="btn btn-gold">🔁 Ôn theo lịch</Link>
          <Link to={`/deck/${deckId}/flashcard`} className="btn btn-jade">🃏 Flashcard</Link>
          <Link to={`/deck/${deckId}/quiz`} className="btn btn-accent">❓ Quiz</Link>
          <Link to={`/deck/${deckId}/typing`} className="btn btn-amber">⌨️ Typing</Link>
        </div>
      </div>

      <div className="section-header">
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍 Tìm kiếm thẻ..."
          style={{ padding: '0.5rem 0.9rem', border: '1px solid var(--border)', borderRadius: '8px', fontFamily: 'inherit', fontSize: '0.875rem', width: '220px' }}
        />
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-ai" onClick={() => setShowAI(true)}>✨ AI</button>
          <button className="btn btn-primary" onClick={() => { setEditCard(undefined); setShowModal(true); }}>+ Thêm thẻ</button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🃏</div>
          <p>{search ? 'Không tìm thấy thẻ nào.' : 'Bộ từ chưa có thẻ nào.'}</p>
          {!search && (
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button className="btn btn-ai" onClick={() => setShowAI(true)}>✨ Tạo bằng AI</button>
              <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ Thêm thẻ thủ công</button>
            </div>
          )}
        </div>
      ) : (
        <div className="card-table-wrap">
          <table className="card-table">
            <thead>
              <tr>
                <th>Mặt trước</th>
                {isJa && <th>Đọc</th>}
                <th>Nghĩa</th>
                <th>Câu ví dụ</th>
                <th>SRS</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(card => (
                <tr key={card.id}>
                  <td className="card-front-cell">{card.front}</td>
                  {isJa && <td className="card-reading-cell">{card.reading || '—'}</td>}
                  <td>{card.back}</td>
                  <td style={{ color: 'var(--ink-muted)', fontSize: '0.8rem', maxWidth: '200px' }}>
                    {card.example ? card.example.slice(0, 60) + (card.example.length > 60 ? '…' : '') : '—'}
                  </td>
                  <td>
                    <span style={{ fontSize: '0.75rem', color: 'var(--ink-muted)' }}>
                      ×{card.repetitions} · EF {card.ease_factor?.toFixed(1)}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setEditCard(card); setShowModal(true); }}>✏️</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(card.id)}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <CardModal deckId={deckId} card={editCard} onClose={() => setShowModal(false)} onSaved={load} />
      )}
      {showAI && (
        <AIGenerateModal deckId={deckId} langPair={deck.language_pair} onClose={() => setShowAI(false)} onCardsAdded={load} />
      )}
    </div>
  );
}
